import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAutoLogout from '../hooks/useAutoLogout';
import {
  Users, Search, Megaphone, Download, ChevronRight,
  Mail, MessageCircle, Phone, CheckCircle, ArrowRight, ArrowLeft, Plus, X,
} from 'lucide-react';

import Toast    from '../components/Toast';
import API_BASE from '../config';
import { useTheme } from '../components/Navbar';

/* ── Theme tokens ───────────────────────────────────────────────── */
const TK = {
  light: {
    detailBg:'#F5F6FA', panelBg:'#FFFFFF', card:'#FFFFFF',
    border:'#ECEEF2', borderSoft:'#F0F2F5', rowBdr:'#F0F2F5',
    text:'#0F1623', sub:'#6B7280', muted:'#9CA3AF',
    accent:'#5B4EE8', accentTxt:'#4338CA', accentBg:'#EEF0FD',
    inputBg:'#F5F6FA', inputBdr:'#ECEEF2', hover:'#F7F8FA', rowHover:'rgba(91,78,232,0.05)',
    pillBg:'#EEF0FD', pillTxt:'#4338CA',
    chipBg:'#FFFFFF', chipBdr:'#ECEEF2', chipTxt:'#6B7280', chipActiveBg:'#5B4EE8', chipActiveTxt:'#FFFFFF',
    hosp:'#059669', hospBg:'rgba(5,150,105,0.10)', hospBdr:'rgba(5,150,105,0.25)',
    rest:'#D97706', restBg:'rgba(217,119,6,0.10)', restBdr:'rgba(217,119,6,0.25)',
    cust:'#7C3AED', custBg:'rgba(124,58,237,0.10)', custBdr:'rgba(124,58,237,0.25)',
    emerald:'#059669', blue:'#2563EB', violet:'#7C3AED', err:'#DC2626',
  },
  dark: {
    detailBg:'#080808', panelBg:'#0d0d0d', card:'#0d0d0d',
    border:'#1f1f1f', borderSoft:'#161616', rowBdr:'#161616',
    text:'#F1F2F6', sub:'#8B8FA8', muted:'#6B7280',
    accent:'#7C72FF', accentTxt:'#A5A0FF', accentBg:'rgba(124,114,255,0.12)',
    inputBg:'#080808', inputBdr:'#1f1f1f', hover:'#171717', rowHover:'rgba(255,255,255,0.02)',
    pillBg:'rgba(124,58,237,0.18)', pillTxt:'#a78bfa',
    chipBg:'#161616', chipBdr:'#2a2a2a', chipTxt:'#9CA3AF', chipActiveBg:'#7c3aed', chipActiveTxt:'#FFFFFF',
    hosp:'#34D399', hospBg:'rgba(52,211,153,0.10)', hospBdr:'rgba(52,211,153,0.20)',
    rest:'#FBBF24', restBg:'rgba(251,191,36,0.10)', restBdr:'rgba(251,191,36,0.20)',
    cust:'#a78bfa', custBg:'rgba(124,58,237,0.12)', custBdr:'rgba(124,58,237,0.25)',
    emerald:'#34D399', blue:'#60A5FA', violet:'#a78bfa', err:'#ef4444',
  },
};

function toIST(d){ if(!d) return '—'; return new Date(d).toLocaleString('en-IN',{timeZone:'Asia/Kolkata',day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true}); }

function segmentType(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('hospital'))   return 'hospital';
  if (n.includes('restaurant')) return 'restaurant';
  return 'general';
}
const catOf = (name) => { const t = segmentType(name); return t === 'general' ? 'custom' : t; };

function typeStyle(tp, tk) {
  if (tp === 'hospital')   return { c: tk.hosp, bg: tk.hospBg, bdr: tk.hospBdr, label: 'Hospital'   };
  if (tp === 'restaurant') return { c: tk.rest, bg: tk.restBg, bdr: tk.restBdr, label: 'Restaurant' };
  return { c: tk.cust, bg: tk.custBg, bdr: tk.custBdr, label: 'Custom' };
}

function formatName(name) {
  return (name || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function getDesc(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('birthday'))    return 'Customers with upcoming birthdays this week';
  if (n.includes('appointment')) return 'Patients with appointments in the next 48 hours';
  if (n.includes('lapsed'))      return 'Customers who haven\'t visited in 30+ days';
  if (n.includes('high_value'))  return 'High-value customers with 5+ visits';
  if (n.includes('new_7d'))      return 'New profiles added in the last 7 days';
  if (n.includes('high_visit'))  return 'Frequent visitors with 10+ visits';
  return 'Custom audience segment';
}

function initials(first, last) {
  return `${(first || '?')[0]}${(last || '')[0] || ''}`.toUpperCase();
}

const CATS = [
  { id: 'all',        label: 'All'        },
  { id: 'hospital',   label: 'Hospital'   },
  { id: 'restaurant', label: 'Restaurant' },
  { id: 'custom',     label: 'Custom'     },
];

export default function Segments() {
  const navigate = useNavigate();
  useAutoLogout();
  const { theme } = useTheme();
  const tk = TK[theme];
  const CARD = { background: tk.card, border: `1px solid ${tk.border}` };

  const [segments,    setSegments]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [filterCat,   setFilterCat]   = useState('all');
  const [selected,    setSelected]    = useState(null);
  const [detail,      setDetail]      = useState(null);
  const [detailLoad,  setDetailLoad]  = useState(false);
  const [detailError, setDetailError] = useState('');
  const [toast,       setToast]       = useState(null);
  const [showCampaign,setShowCampaign]= useState(false);

  /* create-segment modal state */
  const [showCreate,  setShowCreate]  = useState(false);
  const [creating,    setCreating]    = useState(false);
  const [nName,       setNName]       = useState('');
  const [nDesc,       setNDesc]       = useState('');
  const [nMinVisits,  setNMinVisits]  = useState('');
  const [nCity,       setNCity]       = useState('');
  const [nWaOnly,     setNWaOnly]     = useState(false);
  const [nAgeVerified, setNAgeVerified] = useState(false);
  const [nSegType,     setNSegType]     = useState('');
  const [nActiveDays,  setNActiveDays]  = useState('');
  const [nLapsedDays,  setNLapsedDays]  = useState('');
  const [nMatchMode,   setNMatchMode]   = useState('all');
  const [previewCount, setPreviewCount] = useState(null);
  const [previewing,   setPreviewing]   = useState(false);
  const [deleting,     setDeleting]     = useState(false);

  const token = localStorage.getItem('martech_token') || sessionStorage.getItem('token');

  const loadSegments = useCallback(async () => {
    if (!token) { navigate('/'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/v1/segments`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const d = await r.json();
      if (d.success) setSegments(d.segments || []);
    } catch {} finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => { loadSegments(); }, [loadSegments]);

  const loadSegment = useCallback(async (seg) => {
    if (!seg) return;
    setSelected(seg);
    setDetail(null);
    setDetailError('');
    setShowCampaign(false);
    setDetailLoad(true);
    const tok = localStorage.getItem('martech_token') || sessionStorage.getItem('token');
    try {
      const res = await fetch(
        `${API_BASE}/api/v1/segments/${encodeURIComponent(seg.segment_name)}`,
        { headers: { Authorization: `Bearer ${tok}` } }
      );
      const data = await res.json();
      const profiles = data.profiles || data.data || data.members || [];
      setDetail({ ...data, profiles });
    } catch (err) {
      setDetailError(err.message || 'Failed to load profiles.');
    } finally {
      setDetailLoad(false);
    }
  }, []);

  const resetCreate = () => { setNName(''); setNDesc(''); setNMinVisits(''); setNCity(''); setNWaOnly(false); setNAgeVerified(false); setNSegType(''); setNActiveDays(''); setNLapsedDays(''); setNMatchMode('all'); setPreviewCount(null); };

  const previewSegment = async () => {
    setPreviewCount(null); setPreviewing(true);
    try {
      const rules = {};
      if (nMinVisits !== '') rules.min_visits = Number(nMinVisits);
      if (nCity.trim())      rules.cities = nCity.trim();
      if (nWaOnly)           rules.whatsapp_only = true;
      if (nAgeVerified)       rules.age_verified_only = true;
      if (nSegType.trim())    rules.segment_type = nSegType.trim();
      if (nActiveDays !== '') rules.active_within_days = Number(nActiveDays);
      if (nLapsedDays !== '') rules.lapsed_days = Number(nLapsedDays);
      const res = await fetch(`${API_BASE}/api/v1/segments/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules, match_mode: nMatchMode }),
      });
      const data = await res.json().catch(() => ({}));
      setPreviewCount(data.count ?? 0);
    } catch { setPreviewCount(0); } finally { setPreviewing(false); }
  };

  const deleteSegment = async (name) => {
    const seg = segments.find(x => x.segment_name === name);
    const cnt = parseInt(seg?.count) || 0;
    if (!window.confirm('Delete segment "' + name + '"? This removes ' + cnt + ' profile memberships.')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/segments/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setToast({ msg: 'Segment deleted (' + cnt + ' memberships removed)', variant: 'success' });
      if (selected?.segment_name === name) setSelected(null);
      await loadSegments();
    } catch (e) { setToast({ msg: e.message, variant: 'error' }); }
    finally { setDeleting(false); }
  };

  const createSegment = async () => {
    const slug = nName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (!slug) { setToast({ msg: 'Please enter a segment name', variant: 'error' }); return; }
    setCreating(true);
    try {
      const rules = {};
      if (nMinVisits !== '') rules.min_visits = Number(nMinVisits);
      if (nCity.trim())      rules.cities = nCity.trim();
      if (nWaOnly)           rules.whatsapp_only = true;
      if (nAgeVerified)       rules.age_verified_only = true;
      if (nSegType.trim())    rules.segment_type = nSegType.trim();
      if (nActiveDays !== '') rules.active_within_days = Number(nActiveDays);
      if (nLapsedDays !== '') rules.lapsed_days = Number(nLapsedDays);
      const res = await fetch(`${API_BASE}/api/v1/segments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ segment_name: slug, description: nDesc.trim(), rules, match_mode: nMatchMode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || `Create failed (${res.status})`);
      }
      setToast({ msg: 'Custom segment created', variant: 'success' });
      setShowCreate(false);
      resetCreate();
      await loadSegments();
    } catch (e) {
      setToast({ msg: e.message || 'Could not create segment', variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const exportCSV = () => {
    if (!detail?.profiles) return;
    const cols = ['profile_id', 'first_name', 'last_name', 'email', 'phone', 'city', 'visit_count', 'whatsapp_opted_in', 'last_seen', 'created_at'];
    const rows = [
      cols.join(','),
      ...detail.profiles.map(p => cols.map(c => `"${(p[c] ?? '')}"`).join(',')),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), {
      href: url, download: `${selected?.segment_name || 'segment'}-profiles.csv`,
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const searchFiltered = segments.filter(s =>
    (s.segment_name || '').toLowerCase().includes(search.toLowerCase())
  );
  const counts = {
    all:        searchFiltered.length,
    hospital:   searchFiltered.filter(s => catOf(s.segment_name) === 'hospital').length,
    restaurant: searchFiltered.filter(s => catOf(s.segment_name) === 'restaurant').length,
    custom:     searchFiltered.filter(s => catOf(s.segment_name) === 'custom').length,
  };
  const filtered = searchFiltered.filter(s => filterCat === 'all' || catOf(s.segment_name) === filterCat);

  const hospitalSegs   = filtered.filter(s => segmentType(s.segment_name) === 'hospital');
  const restaurantSegs = filtered.filter(s => segmentType(s.segment_name) === 'restaurant');
  const customSegs     = filtered.filter(s => segmentType(s.segment_name) === 'general');

  const profiles    = detail?.profiles || [];
  const waOptIn     = profiles.filter(p => p.whatsapp_opted_in).length;
  const ageVerified = profiles.filter(p => p.age_verified).length;
  const avgVisits   = profiles.length > 0
    ? (profiles.reduce((s, p) => s + (p.visit_count || 0), 0) / profiles.length).toFixed(1)
    : 0;

  const inputStyle = { background: tk.inputBg, border: `1px solid ${tk.inputBdr}`, color: tk.text };

  function SegmentGroup({ label, segs }) {
    if (segs.length === 0) return null;
    return (
      <div className="mb-3">
        <p className="text-[9px] font-bold tracking-[0.15em] uppercase px-4 py-2" style={{ color: tk.muted }}>{label}</p>
        {segs.map((seg, i) => {
          const cnt      = parseInt(seg.count) || 0;
          const ts       = typeStyle(segmentType(seg.segment_name), tk);
          const isActive = selected?.segment_name === seg.segment_name;
          return (
            <button
              key={i}
              onClick={() => loadSegment(seg)}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = tk.hover; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-150 relative"
              style={{ background: isActive ? tk.accentBg : 'transparent', borderLeft: `2px solid ${isActive ? tk.accent : 'transparent'}` }}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ts.c }} />
              <span className="flex-1 text-sm truncate" style={{ color: isActive ? tk.text : tk.sub, fontWeight: isActive ? 600 : 400 }}>
                {formatName(seg.segment_name)}
              </span>
              <span className="text-xs font-black px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: tk.pillBg, color: tk.pillTxt }}>{cnt}</span>
            </button>
          );
        })}
      </div>
    );
  }

  function ProfileCard({ p }) {
    return (
      <div className="rounded-xl p-3 flex items-start gap-3" style={CARD}>
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-black text-xs flex-shrink-0" style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)' }}>
          {initials(p.first_name, p.last_name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-sm truncate" style={{ color: tk.text }}>
              {[p.first_name, p.last_name].filter(Boolean).join(' ') || 'Anonymous'}
            </span>
            <span className="text-[11px] font-black px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: tk.pillBg, color: tk.pillTxt }}>
              {p.visit_count || 0} visits
            </span>
          </div>
          <p className="text-xs truncate mt-0.5" style={{ color: tk.sub }}>{p.email || '—'}</p>
          <div className="flex items-center gap-1.5 flex-wrap mt-1 text-xs" style={{ color: tk.muted }}>
            <span>{p.phone || '—'}</span>
            {p.city ? <><span>·</span><span>{p.city}</span></> : null}
          </div>
          <div className="flex items-center justify-between gap-2 mt-1.5">
            <span className="text-[11px]" style={{ color: tk.muted }}>{toIST(p.last_seen)}</span>
            {p.whatsapp_opted_in
              ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: tk.emerald }}><CheckCircle className="w-3.5 h-3.5" /> WhatsApp</span>
              : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: tk.detailBg }}>
      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={() => setToast(null)} />}

      <main className="flex flex-col md:flex-row md:h-screen">

        {/* ─── LEFT COLUMN: Segment list ─── */}
        <div
          className={`${selected ? 'hidden md:flex' : 'flex'} w-full md:w-2/5 flex-col md:flex-shrink-0 md:h-screen md:overflow-hidden border-b md:border-b-0 md:border-r`}
          style={{ background: tk.panelBg, borderColor: tk.border }}
        >
          {/* Header */}
          <div className="px-5 pt-6 pb-4 flex-shrink-0" style={{ borderBottom: `1px solid ${tk.border}` }}>
            <div className="flex items-center justify-between gap-2">
              <h1 className="text-2xl font-black tracking-tight" style={{ color: tk.text }}>Segments</h1>
              <button
                onClick={() => { resetCreate(); setShowCreate(true); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold text-white flex-shrink-0 transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#8b5cf6)' }}
              >
                <Plus className="w-4 h-4" /> New
              </button>
            </div>
            {!loading && <p className="text-xs mt-1" style={{ color: tk.muted }}>{segments.length} total · tap to explore</p>}

            {/* Search */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: tk.muted }} />
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search segments…"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all" style={inputStyle}
                onFocus={e => { e.target.style.borderColor = tk.accent; }}
                onBlur={e  => { e.target.style.borderColor = tk.inputBdr; }}
              />
            </div>

            {/* Category filter pills */}
            <div className="flex flex-wrap gap-2 mt-3">
              {CATS.map(({ id, label }) => {
                const active = filterCat === id;
                return (
                  <button
                    key={id} onClick={() => setFilterCat(id)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full transition-all"
                    style={{ background: active ? tk.chipActiveBg : tk.chipBg, color: active ? tk.chipActiveTxt : tk.chipTxt, border: `1px solid ${active ? tk.chipActiveBg : tk.chipBdr}` }}
                  >
                    {label} <span style={{ opacity: 0.7 }}>· {counts[id]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Segment list */}
          <div className="flex-1 md:overflow-y-auto py-2">
            {loading ? (
              <div className="space-y-1 px-3 py-2">
                {Array(10).fill(null).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="shimmer w-2 h-2 rounded-full flex-shrink-0" style={{ background: tk.borderSoft }} />
                    <div className="shimmer h-4 flex-1 rounded" style={{ background: tk.borderSoft }} />
                    <div className="shimmer h-5 w-8 rounded-full" style={{ background: tk.borderSoft }} />
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Users className="w-10 h-10 mb-3" style={{ color: tk.muted }} />
                <p className="text-sm" style={{ color: tk.muted }}>No segments found</p>
              </div>
            ) : (
              <>
                <SegmentGroup label="Hospital"   segs={hospitalSegs} />
                <SegmentGroup label="Restaurant" segs={restaurantSegs} />
                <SegmentGroup label="Custom"     segs={customSegs} />
              </>
            )}
          </div>

          {/* Footer */}
          {!loading && (
            <div className="px-5 py-3 flex-shrink-0" style={{ borderTop: `1px solid ${tk.border}` }}>
              <p className="text-xs" style={{ color: tk.muted }}>
                Total profiles across all segments:{' '}
                <span className="font-bold" style={{ color: tk.accent }}>
                  {segments.reduce((s, x) => s + (parseInt(x.count) || 0), 0)}
                </span>
              </p>
            </div>
          )}
        </div>

        {/* ─── RIGHT COLUMN: Detail ─── */}
        <div
          className={`${selected ? 'flex' : 'hidden md:flex'} flex-1 flex-col md:h-screen md:overflow-y-auto`}
          style={{ background: tk.detailBg }}
        >
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-20">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: tk.accentBg }}>
                <ArrowRight className="w-8 h-8" style={{ color: tk.accent }} />
              </div>
              <h2 className="font-bold text-lg mb-2" style={{ color: tk.text }}>Select a segment to explore</h2>
              <p className="text-sm" style={{ color: tk.muted }}>Choose a segment from the list to view profiles and run campaigns</p>
            </div>
          ) : (
            <div className="flex-1 px-5 md:px-6 py-5 md:py-6">

              <button onClick={() => setSelected(null)} className="md:hidden flex items-center gap-1.5 mb-4 text-sm font-semibold" style={{ color: tk.accent }}>
                <ArrowLeft className="w-4 h-4" /> All segments
              </button>

              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                <div>
                  <div className="flex items-center flex-wrap gap-2 md:gap-3 mb-2">
                    <h2 className="text-2xl font-black" style={{ color: tk.text }}>{formatName(selected.segment_name)}</h2>
                    <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full" style={{ background: tk.accentBg, color: tk.accentTxt }}>
                      {detail?.count ?? selected.count} profiles
                    </span>
                    {(() => { const ts = typeStyle(segmentType(selected.segment_name), tk); return (
                      <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full" style={{ background: ts.bg, color: ts.c, border: `1px solid ${ts.bdr}` }}>{ts.label}</span>
                    ); })()}
                  </div>
                  <p className="text-sm" style={{ color: tk.sub }}>{getDesc(selected.segment_name)}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 w-full md:w-auto">
                  <button
                    onClick={exportCSV} disabled={!detail?.profiles?.length}
                    className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] disabled:opacity-40"
                    style={{ background: tk.card, border: `1px solid ${tk.border}`, color: tk.sub }}
                  >
                    <Download className="w-4 h-4" /> Export
                  </button>
                  <button onClick={() => deleteSegment(selected.segment_name)} disabled={deleting}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] disabled:opacity-40"
                    style={{ background: tk.card, border: `1px solid ${tk.err}`, color: tk.err }}>
                    <X className="w-4 h-4" /><span className="hidden sm:inline ml-1">Delete</span>
                  </button>
                  <button
                    onClick={() => setShowCampaign(true)}
                    className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02]"
                    style={{ background: 'linear-gradient(135deg,#7c3aed,#8b5cf6)' }}
                  >
                    <Megaphone className="w-4 h-4" /> Run Campaign
                  </button>
                </div>
              </div>

              {/* Desktop table */}
              <div className="hidden md:block rounded-2xl overflow-hidden mb-4" style={CARD}>
                <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${tk.border}` }}>
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: tk.muted }}>Profiles</p>
                  {detailLoad && <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: tk.accent, borderTopColor: 'transparent' }} />}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${tk.border}` }}>
                        {['', 'Name', 'Email', 'Phone', 'City', 'Last Seen', 'Visits', 'WA'].map((col, i) => (
                          <th key={i} className="px-4 py-3 text-left text-[10px] font-bold tracking-widest uppercase" style={{ color: tk.muted }}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detailLoad ? (
                        Array(5).fill(null).map((_, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${tk.rowBdr}` }}>
                            {[36,120,140,90,70,100,60,30].map((w,j)=>(<td key={j} className="px-4 py-3.5"><div className="shimmer h-4 rounded" style={{ width:w, background:tk.borderSoft }}/></td>))}
                          </tr>
                        ))
                      ) : detailError ? (
                        <tr><td colSpan={8} className="py-16 text-center"><p className="text-sm" style={{ color: tk.err }}>{detailError}</p></td></tr>
                      ) : profiles.length === 0 ? (
                        <tr><td colSpan={8} className="py-16 text-center">
                          <Users className="w-8 h-8 mx-auto mb-2" style={{ color: tk.muted }} />
                          <p className="text-sm" style={{ color: tk.muted }}>No profiles in this segment yet</p>
                        </td></tr>
                      ) : (
                        profiles.map((p, idx) => (
                          <tr key={p.profile_id || idx}
                            onMouseEnter={e => e.currentTarget.style.background = tk.rowHover}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            style={{ borderBottom: `1px solid ${tk.rowBdr}`, transition: 'background 0.15s' }}>
                            <td className="px-4 py-3"><div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-black text-xs" style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)' }}>{initials(p.first_name, p.last_name)}</div></td>
                            <td className="px-4 py-3 font-semibold whitespace-nowrap text-xs" style={{ color: tk.text }}>{[p.first_name, p.last_name].filter(Boolean).join(' ') || 'Anonymous'}</td>
                            <td className="px-4 py-3 text-xs" style={{ color: tk.sub, maxWidth: 140 }}><span className="block truncate">{p.email || '—'}</span></td>
                            <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: tk.sub }}>{p.phone || '—'}</td>
                            <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: tk.sub }}>{p.city || '—'}</td>
                            <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: tk.sub }}>{toIST(p.last_seen)}</td>
                            <td className="px-4 py-3 font-black text-sm" style={{ color: tk.accent }}>{p.visit_count || 0}</td>
                            <td className="px-4 py-3">{p.whatsapp_opted_in ? <CheckCircle className="w-4 h-4" style={{ color: tk.emerald }} /> : <span className="text-sm" style={{ color: tk.muted }}>—</span>}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden mb-4">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: tk.muted }}>Profiles</p>
                {detailLoad ? (
                  <div className="space-y-2">{Array(4).fill(null).map((_, i) => <div key={i} className="shimmer h-20 rounded-xl" style={{ background: tk.borderSoft }} />)}</div>
                ) : detailError ? (
                  <div className="rounded-xl p-4 text-center text-sm" style={{ ...CARD, color: tk.err }}>{detailError}</div>
                ) : profiles.length === 0 ? (
                  <div className="rounded-xl p-8 text-center" style={CARD}>
                    <Users className="w-8 h-8 mx-auto mb-2" style={{ color: tk.muted }} />
                    <p className="text-sm" style={{ color: tk.muted }}>No profiles in this segment yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">{profiles.map((p, i) => <ProfileCard key={p.profile_id || i} p={p} />)}</div>
                )}
              </div>

              {/* Mini stats */}
              {!detailLoad && profiles.length > 0 && (
                <div className="grid grid-cols-3 gap-2 md:gap-3">
                  {[
                    { label: 'WhatsApp Opted In', value: `${waOptIn}/${profiles.length}`,     color: tk.emerald },
                    { label: 'Age Verified',      value: `${ageVerified}/${profiles.length}`, color: tk.blue    },
                    { label: 'Avg Visits',        value: avgVisits,                           color: tk.violet  },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="rounded-xl p-3 md:p-4" style={CARD}>
                      <p className="text-base md:text-xl font-black" style={{ color }}>{value}</p>
                      <p className="text-[11px] md:text-xs mt-1" style={{ color: tk.muted }}>{label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Campaign Modal */}
              {showCampaign && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
                  <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCampaign(false)} />
                  <div className="relative rounded-2xl p-6 w-full max-w-sm z-10" style={CARD}>
                    <h3 className="font-black text-base mb-1" style={{ color: tk.text }}>Run Campaign</h3>
                    <p className="text-xs mb-4" style={{ color: tk.sub }}>Segment: <span className="font-bold" style={{ color: tk.accent }}>{formatName(selected.segment_name)}</span></p>
                    <div className="space-y-3">
                      {[
                        { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: tk.emerald },
                        { id: 'email',    label: 'Email',    icon: Mail,          color: tk.blue    },
                        { id: 'sms',      label: 'SMS',      icon: Phone,         color: tk.violet  },
                      ].map(({ id, label, icon: Icon, color }) => (
                        <button key={id} onClick={() => { navigate(`/campaigns`); setShowCampaign(false); }}
                          className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all hover:scale-[1.01]"
                          style={{ background: tk.detailBg, border: `1px solid ${tk.border}` }}>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: color + '1A' }}><Icon className="w-4 h-4" style={{ color }} /></div>
                          <span className="text-sm font-semibold" style={{ color: tk.text }}>{label}</span>
                          <ChevronRight className="w-4 h-4 ml-auto" style={{ color: tk.muted }} />
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setShowCampaign(false)} className="w-full mt-3 py-2.5 rounded-xl text-sm font-semibold" style={{ color: tk.sub }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* ─── Create Custom Segment Modal ─── */}
      {showCreate && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !creating && setShowCreate(false)} />
          <div className="relative rounded-2xl p-6 w-full max-w-md z-10 max-h-[90vh] overflow-y-auto" style={CARD}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-black text-base" style={{ color: tk.text }}>Create Custom Segment</h3>
              <button onClick={() => !creating && setShowCreate(false)} style={{ color: tk.muted }}><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs mb-4" style={{ color: tk.sub }}>Define a named audience with optional conditions.</p>

            <label className="block text-xs font-semibold mb-1" style={{ color: tk.sub }}>Segment name *</label>
            <input value={nName} onChange={e => setNName(e.target.value)} placeholder="e.g. VIP Diners Mumbai"
              className="w-full px-3 py-2.5 rounded-xl text-sm outline-none mb-3" style={inputStyle} />

            <label className="block text-xs font-semibold mb-1" style={{ color: tk.sub }}>Description</label>
            <input value={nDesc} onChange={e => setNDesc(e.target.value)} placeholder="Optional"
              className="w-full px-3 py-2.5 rounded-xl text-sm outline-none mb-3" style={inputStyle} />

            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: tk.muted }}>Conditions</p>
              <div className="flex gap-1 rounded-lg p-0.5" style={{ background: tk.inputBg, border: `1px solid ${tk.inputBdr}` }}>
                {['all','any'].map(m => (
                  <button key={m} onClick={() => setNMatchMode(m)}
                    className="px-3 py-1 rounded-md text-xs font-bold transition-all"
                    style={{ background: nMatchMode===m ? tk.accent : 'transparent', color: nMatchMode===m ? '#fff' : tk.muted }}>
                    {m === 'all' ? 'Match All' : 'Match Any'}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: tk.sub }}>Min visits</label>
                <input type="number" min="0" value={nMinVisits} onChange={e => setNMinVisits(e.target.value)} placeholder="0" className="w-full px-3 py-2.5 rounded-xl text-sm outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: tk.sub }}>Cities (comma-separated)</label>
                <input value={nCity} onChange={e => setNCity(e.target.value)} placeholder="Vijayawada, Hyderabad" className="w-full px-3 py-2.5 rounded-xl text-sm outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: tk.sub }}>Active within (days)</label>
                <input type="number" min="1" value={nActiveDays} onChange={e => setNActiveDays(e.target.value)} placeholder="e.g. 30" className="w-full px-3 py-2.5 rounded-xl text-sm outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: tk.sub }}>Lapsed (not seen in days)</label>
                <input type="number" min="1" value={nLapsedDays} onChange={e => setNLapsedDays(e.target.value)} placeholder="e.g. 90" className="w-full px-3 py-2.5 rounded-xl text-sm outline-none" style={inputStyle} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold mb-1" style={{ color: tk.sub }}>Segment type</label>
                <select value={nSegType} onChange={e => setNSegType(e.target.value)} className="w-full px-3 py-2.5 rounded-xl text-sm outline-none" style={inputStyle}>
                  <option value="">Any type</option>
                  <option value="hospital">Hospital</option>
                  <option value="restaurant">Restaurant</option>
                </select>
              </div>
            </div>
            <div className="space-y-2 mb-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={nWaOnly} onChange={e => setNWaOnly(e.target.checked)} style={{ accentColor: tk.accent, width: 16, height: 16 }} />
                <span className="text-sm" style={{ color: tk.text }}>WhatsApp opted-in only</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={nAgeVerified} onChange={e => setNAgeVerified(e.target.checked)} style={{ accentColor: tk.accent, width: 16, height: 16 }} />
                <span className="text-sm" style={{ color: tk.text }}>Age verified only</span>
              </label>
            </div>

            {previewCount !== null && (
              <div className="rounded-xl px-4 py-2.5 mb-3 flex items-center gap-2" style={{ background: tk.accentBg }}>
                <span className="text-xl font-black" style={{ color: tk.accent }}>{previewCount}</span>
                <span className="text-sm" style={{ color: tk.sub }}> profiles match your filters</span>
              </div>
            )}
            <button onClick={previewSegment} disabled={previewing || creating}
              className="w-full mb-3 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
              style={{ background: tk.inputBg, border: `1px solid ${tk.inputBdr}`, color: tk.accentTxt }}>
              {previewing ? 'Previewing…' : '🔍 Preview matching profiles'}
            </button>
            <div className="flex gap-2">
              <button onClick={() => setShowCreate(false)} disabled={creating}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
                style={{ background: tk.inputBg, border: `1px solid ${tk.border}`, color: tk.sub }}>Cancel</button>
              <button onClick={createSegment} disabled={creating}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#8b5cf6)', opacity: creating ? 0.7 : 1 }}>
                {creating ? 'Creating…' : 'Create Segment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}